import { useEffect } from 'react';
import CheckCircle from '../Icons/CheckCircle';
import { FiAlertCircle } from 'react-icons/fi';

type toastPosition = 'topLeft' | 'topRight' | 'bottomRight' | 'bottomLeft' | 'bottom' | 'top';

const positionStyles: Record<toastPosition, string> = {
  topLeft: 'bottom-[130px] md:bottom-auto md:top-[160px] left-4',
  topRight: 'bottom-[130px] md:bottom-auto md:top-[160px] right-4',
  bottomLeft: 'bottom-20 md:bottom-4 left-4',
  bottomRight: 'bottom-20 md:bottom-4 right-4',
  bottom: 'bottom-20 md:bottom-4',
  top: 'top-20 md:top-4',
};

interface ToastProps {
  message: string;
  type?: 'default' | 'success' | 'error' | 'warning' | 'pinkError';
  duration?: number;
  onClose: () => void;
  isVisible?: boolean;
  showCloseButton?: boolean;
  position?: toastPosition;
  className?: string;
}

const Toast = ({
  message,
  type = 'default',
  duration = 3000,
  onClose,
  isVisible,
  showCloseButton = true,
  position = 'bottomRight',
  className = '',
}: ToastProps) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Define toast styles based on type
  const toastStyles = {
    default: 'bg-blue-500 text-white',
    success: 'bg-[#E9FFE5] text-[#1EAA0B]',
    error: 'bg-supportiveRed text-white',
    pinkError: 'bg-[#FFF1F0] text-[#C6173A]',
    warning: 'bg-yellow-500 text-black',
  };

  // If the type is not found, default to 'default'
  const style = toastStyles[type] || toastStyles.default;

  const positionStyle = positionStyles[position] || positionStyles.bottomRight;

  if (!isVisible) return null;

  return (
    <div
      className={`
        fixed z-50 px-4 py-3 rounded shadow-[0_4px_12px_0_rgba(118,121,131,0.25)] 
        transform transition-all duration-300 ease-in-out 
        text-sm font-medium md:min-w-[300px] w-[calc(100%-32px)] md:w-auto
        ${style} ${positionStyle} ${className}
      `}
      role="alert"
    >
      <div className="flex items-center">
        <span className="mr-2">
          {type === 'success' && <CheckCircle />}
          {type === 'error' && '✗'}
          {type === 'warning' && '⚠️'}
          {type === 'pinkError' && <FiAlertCircle size={32} />}
        </span>
        <p>{message}</p>
        {showCloseButton && (
          <button
            onClick={() => onClose()}
            className="ml-4 hover:bg-opacity-25 rounded"
            aria-label="Close toast"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default Toast;
